import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiClock, FiUser, FiCalendar, FiArrowLeft, FiTag } from 'react-icons/fi';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import blogData from '../data/blogData';
import './BlogDetail.css';

const BlogDetail = () => {
  const { slug } = useParams();
  const hydrated = useHydrated();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const post = blogData.find((p) => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = blogData
    .filter((p) => p.id !== post.id && (p.category === post.category || p.tags.some((t) => post.tags.includes(t))))
    .slice(0, 3);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    const elements = [];
    let inTable = false;
    let tableRows = [];
    let inList = false;
    let listItems = [];
    let listType = 'ul';

    const processInlineMarkdown = (text) => {
      const parts = [];
      const regex = /\*\*(.+?)\*\*|\[(.+?)\]\((.+?)\)|`(.+?)`/g;
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          parts.push(text.substring(lastIndex, match.index));
        }
        if (match[1]) {
          parts.push(<strong key={match.index}>{match[1]}</strong>);
        } else if (match[2] && match[3]) {
          parts.push(
            <Link key={match.index} to={match[3]} className="content-link">
              {match[2]}
            </Link>
          );
        } else if (match[4]) {
          parts.push(<code key={match.index} className="inline-code">{match[4]}</code>);
        }
        lastIndex = regex.lastIndex;
      }
      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
      }
      return parts.length > 0 ? parts : text;
    };

    const flushList = () => {
      if (inList && listItems.length > 0) {
        const Tag = listType === 'ol' ? 'ol' : 'ul';
        elements.push(<Tag key={`list-${elements.length}`} className="content-list">{listItems}</Tag>);
        listItems = [];
        inList = false;
      }
    };

    const flushTable = () => {
      if (inTable && tableRows.length > 0) {
        const headerCells = tableRows[0];
        const bodyRows = tableRows.slice(2);
        elements.push(
          <div key={`table-${elements.length}`} className="content-table-wrapper">
            <table className="content-table">
              <thead>
                <tr>
                  {headerCells.map((cell, i) => (
                    <th key={i}>{processInlineMarkdown(cell.trim())}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, rIdx) => (
                  <tr key={rIdx}>
                    {row.map((cell, cIdx) => (
                      <td key={cIdx}>{processInlineMarkdown(cell.trim())}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        tableRows = [];
        inTable = false;
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      if (!trimmed) {
        flushList();
        flushTable();
        continue;
      }

      if (trimmed.startsWith('|')) {
        flushList();
        const cells = trimmed.split('|').filter((c) => c.trim() !== '');
        if (cells.every((c) => /^[-:\s]+$/.test(c.trim()))) {
          if (!inTable) continue;
          tableRows.push(cells);
          inTable = true;
          continue;
        }
        inTable = true;
        tableRows.push(cells);
        continue;
      } else {
        flushTable();
      }

      if (trimmed.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={i} className="content-h2">
            {processInlineMarkdown(trimmed.substring(3))}
          </h2>
        );
      } else if (trimmed.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={i} className="content-h3">
            {processInlineMarkdown(trimmed.substring(4))}
          </h3>
        );
      } else if (trimmed.startsWith('> ')) {
        flushList();
        elements.push(
          <blockquote key={i} className="content-blockquote">
            {processInlineMarkdown(trimmed.substring(2))}
          </blockquote>
        );
      } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        if (!inList) {
          inList = true;
          listType = 'ul';
        }
        listItems.push(
          <li key={i}>{processInlineMarkdown(trimmed.substring(2))}</li>
        );
      } else if (/^\d+\.\s/.test(trimmed)) {
        if (!inList) {
          inList = true;
          listType = 'ol';
        }
        listItems.push(
          <li key={i}>{processInlineMarkdown(trimmed.replace(/^\d+\.\s/, ''))}</li>
        );
      } else {
        flushList();
        elements.push(
          <p key={i} className="content-paragraph">
            {processInlineMarkdown(trimmed)}
          </p>
        );
      }
    }

    flushList();
    flushTable();
    return elements;
  };

  return (
    <div className="blog-detail-page">
      <SEO
        title={`${post.title} - AstroEpanchang Blog`}
        description={post.excerpt}
        keywords={post.tags.join(', ')}
        canonicalPath={`/blog/${post.slug}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.excerpt,
          author: { '@type': 'Person', name: post.author },
          datePublished: post.date,
          image: post.image,
        }}
      />

      <div className="container">
        {/* Back Link */}
        <motion.div initial={hydrated ? { opacity: 0, x: -20 } : false} animate={{ opacity: 1, x: 0 }}>
          <Link to="/blog" className="back-to-blog">
            <FiArrowLeft /> Back to Blog
          </Link>
        </motion.div>

        {/* Article Header */}
        <motion.article
          className="blog-article"
          initial={hydrated ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="article-header">
            <span className="article-category">{post.category}</span>
            <h1>{post.title}</h1>
            <p className="article-excerpt">{post.excerpt}</p>
            <div className="article-meta">
              <span><FiUser /> {post.author}</span>
              <span><FiCalendar /> {formatDate(post.date)}</span>
              <span><FiClock /> {post.readTime}</span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="article-image">
            <img src={post.image} alt={post.title} />
          </div>

          {/* Article Content */}
          <div className="article-content">
            {renderContent(post.content)}
          </div>

          {/* Tags */}
          <div className="article-tags">
            <FiTag className="tags-icon" />
            {post.tags.map((tag) => (
              <span key={tag} className="article-tag">{tag}</span>
            ))}
          </div>
        </motion.article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <motion.div
            className="related-posts"
            initial={hydrated ? { opacity: 0, y: 20 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2>Related Articles</h2>
            <div className="related-grid">
              {relatedPosts.map((rPost) => (
                <Link key={rPost.id} to={`/blog/${rPost.slug}`} className="related-card">
                  <div className="related-card-image">
                    <img src={rPost.image} alt={rPost.title} />
                  </div>
                  <div className="related-card-content">
                    <span className="related-category">{rPost.category}</span>
                    <h3>{rPost.title}</h3>
                    <span className="related-meta">{rPost.readTime}</span>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BlogDetail;
